import { FormEvent, UIEvent, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

import { AdminUserCreateResponse, AdminUserEditCompanyOption } from '../../services/api';
import { useAdminUserEditOptions, useAdminUserMutations } from '../../hooks/useApi';
import { getMutationErrorMessage } from '../../lib/apiErrors';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
    formCheckboxListSurfaceClassName,
    formFieldGroupControlClassName,
    formFieldGroupSurfaceClassName,
} from '../ui/formPrimitives';
import { Input } from '../ui/input';

interface AdminCreateUserModalProps {
    open: boolean;
    onClose: () => void;
    onCreated?: (result: AdminUserCreateResponse) => void;
}

type CreateUserMode = 'single' | 'batch';
type BatchInputMode = 'emails' | 'csv';
type BatchUserStatus = 'idle' | 'creating' | 'success' | 'error';

interface BatchUserDraft {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    status: BatchUserStatus;
    error?: string;
}

function formatCompanyLabel(company: { companyId: number | null; companyName: string }): string {
    const name = company.companyName.trim() || 'Unnamed company';
    return company.companyId == null ? name : `${name} (#${company.companyId})`;
}

function formatTeamLabel(team: { teamId: number; teamName: string }): string {
    const name = team.teamName.trim() || 'Unnamed team';
    return `${name} (#${team.teamId})`;
}

function toTitleCase(value: string): string {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function stripCsvQuotes(value: string): string {
    const trimmed = value.trim();
    if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
        return trimmed.slice(1, -1).trim();
    }
    return trimmed;
}

function inferNamesFromEmail(email: string): { firstName: string; lastName: string } {
    const [localPart] = email.split('@');
    const parts = localPart.split(/[._-]+/).filter(Boolean);
    return {
        firstName: toTitleCase(parts[0] ?? ''),
        lastName: parts.length > 1 ? parts.slice(1).map(toTitleCase).join(' ') : '',
    };
}

function parseBatchUsers(input: string, mode: BatchInputMode): BatchUserDraft[] {
    const lines =
        mode === 'emails'
            ? input.split(/[\n,;]+/)
            : input.split(/\r?\n/).filter((line) => line.trim().length > 0);

    return lines
        .map((line, index) => {
            if (mode === 'emails') {
                const email = line.trim().toLowerCase();
                const names = inferNamesFromEmail(email);
                return {
                    id: `${index}-${email}`,
                    email,
                    ...names,
                    status: 'idle' as const,
                };
            }

            const [emailValue = '', firstName = '', lastName = ''] = line.split(',');
            return {
                id: `${index}-${emailValue.trim().toLowerCase()}`,
                email: stripCsvQuotes(emailValue).toLowerCase(),
                firstName: stripCsvQuotes(firstName),
                lastName: stripCsvQuotes(lastName),
                status: 'idle' as const,
            };
        })
        .filter((user) => user.email.length > 0);
}

export function AdminCreateUserModal({ open, onClose, onCreated }: AdminCreateUserModalProps) {
    const { createUser } = useAdminUserMutations();
    const [mode, setMode] = useState<CreateUserMode>('single');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [batchInputMode, setBatchInputMode] = useState<BatchInputMode>('emails');
    const [batchInput, setBatchInput] = useState('');
    const [batchUsers, setBatchUsers] = useState<BatchUserDraft[]>([]);
    const [isBatchCreating, setIsBatchCreating] = useState(false);
    const [company, setCompany] = useState<AdminUserEditCompanyOption | null>(null);
    const [teamIds, setTeamIds] = useState<number[]>([]);
    const [isActive, setIsActive] = useState(true);
    const [inviteUser, setInviteUser] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setMode('single');
        setFirstName('');
        setLastName('');
        setEmail('');
        setBatchInputMode('emails');
        setBatchInput('');
        setBatchUsers([]);
        setIsBatchCreating(false);
        setCompany(null);
        setTeamIds([]);
        setIsActive(true);
        setInviteUser(true);
        setError(null);
    }, [open]);

    if (!open) return null;

    const toggleTeam = (teamId: number) => {
        setTeamIds((current) =>
            current.includes(teamId)
                ? current.filter((candidate) => candidate !== teamId)
                : [...current, teamId],
        );
    };

    const updateBatchUser = (
        userId: string,
        field: keyof Pick<BatchUserDraft, 'email' | 'firstName' | 'lastName'>,
        value: string,
    ) => {
        setBatchUsers((current) =>
            current.map((user) =>
                user.id === userId
                    ? {
                          ...user,
                          [field]: field === 'email' ? value.trim().toLowerCase() : value,
                          status: 'idle',
                          error: undefined,
                      }
                    : user,
            ),
        );
    };

    const handleParseBatchUsers = () => {
        const parsedUsers = parseBatchUsers(batchInput, batchInputMode);
        setBatchUsers(parsedUsers);
        setError(parsedUsers.length === 0 ? 'Add at least one user to create.' : null);
    };

    const handleBatchSubmit = async () => {
        if (batchUsers.length === 0) {
            setError('Parse at least one user to create.');
            return;
        }
        if (!company) {
            setError('Choose a company.');
            return;
        }
        if (teamIds.length === 0) {
            setError('Choose at least one team.');
            return;
        }

        const invalidUser = batchUsers.find(
            (user) => !user.email.trim() || !user.firstName.trim() || !user.lastName.trim(),
        );
        if (invalidUser) {
            setError('Every batch user needs an email, first name, and last name.');
            return;
        }

        setIsBatchCreating(true);
        let lastCreated: AdminUserCreateResponse | null = null;
        let failures = 0;

        for (const user of batchUsers) {
            setBatchUsers((current) =>
                current.map((candidate) =>
                    candidate.id === user.id
                        ? { ...candidate, status: 'creating', error: undefined }
                        : candidate,
                ),
            );

            try {
                lastCreated = await createUser.mutateAsync({
                    firstName: user.firstName.trim(),
                    lastName: user.lastName.trim(),
                    email: user.email.trim().toLowerCase(),
                    companyId: company.companyId,
                    teamIds,
                    isActive,
                    inviteUser,
                });
                setBatchUsers((current) =>
                    current.map((candidate) =>
                        candidate.id === user.id
                            ? { ...candidate, status: 'success', error: undefined }
                            : candidate,
                    ),
                );
            } catch (err) {
                failures += 1;
                const message = getMutationErrorMessage(err, 'Failed to create user.');
                setBatchUsers((current) =>
                    current.map((candidate) =>
                        candidate.id === user.id
                            ? { ...candidate, status: 'error', error: message }
                            : candidate,
                    ),
                );
            }
        }

        setIsBatchCreating(false);

        if (failures > 0) {
            setError(`${failures} user${failures === 1 ? '' : 's'} failed to create.`);
            return;
        }

        if (lastCreated) {
            onCreated?.(lastCreated);
        }
        onClose();
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setError(null);

        if (mode === 'batch') {
            await handleBatchSubmit();
            return;
        }

        const trimmedFirstName = firstName.trim();
        const trimmedLastName = lastName.trim();
        const trimmedEmail = email.trim().toLowerCase();

        if (!trimmedFirstName) {
            setError('First name is required.');
            return;
        }
        if (!trimmedLastName) {
            setError('Last name is required.');
            return;
        }
        if (!trimmedEmail) {
            setError('Email is required.');
            return;
        }
        if (!company) {
            setError('Choose a company.');
            return;
        }
        if (teamIds.length === 0) {
            setError('Choose at least one team.');
            return;
        }

        try {
            const result = await createUser.mutateAsync({
                firstName: trimmedFirstName,
                lastName: trimmedLastName,
                email: trimmedEmail,
                companyId: company.companyId,
                teamIds,
                isActive,
                inviteUser,
            });
            onCreated?.(result);
            onClose();
        } catch (err) {
            setError(getMutationErrorMessage(err, 'Failed to create user.'));
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white shadow-xl">
                <div className="flex items-start justify-between gap-4 border-b p-5">
                    <div>
                        <h2 className="text-xl font-semibold text-neutral-950">Create User</h2>
                        <p className="mt-1 text-sm text-neutral-500">Creates user in Auth0.</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                    >
                        <X className="size-5" />
                    </button>
                </div>

                <form className="space-y-5 p-5" onSubmit={handleSubmit}>
                    <div className="inline-flex rounded-lg border border-neutral-200 p-1">
                        <ModeButton
                            active={mode === 'single'}
                            label="Create one user"
                            onClick={() => {
                                setMode('single');
                                setError(null);
                            }}
                        />
                        <ModeButton
                            active={mode === 'batch'}
                            label="Create multiple users"
                            onClick={() => {
                                setMode('batch');
                                setError(null);
                            }}
                        />
                    </div>

                    {error && (
                        <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</p>
                    )}

                    {mode === 'single' ? (
                        <>
                            <div className="grid gap-4 md:grid-cols-2">
                                <Field label="First name" required>
                                    <Input
                                        value={firstName}
                                        onChange={(event) => setFirstName(event.target.value)}
                                    />
                                </Field>
                                <Field label="Last name" required>
                                    <Input
                                        value={lastName}
                                        onChange={(event) => setLastName(event.target.value)}
                                    />
                                </Field>
                            </div>

                            <Field label="Email" required>
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(event) => setEmail(event.target.value)}
                                />
                            </Field>
                        </>
                    ) : (
                        <BatchUserEditor
                            input={batchInput}
                            inputMode={batchInputMode}
                            onInputChange={setBatchInput}
                            onInputModeChange={(nextMode) => {
                                setBatchInputMode(nextMode);
                                setBatchUsers([]);
                                setError(null);
                            }}
                            onParse={handleParseBatchUsers}
                            onUpdateUser={updateBatchUser}
                            users={batchUsers}
                        />
                    )}

                    <Toggle
                        label="Active"
                        checked={isActive}
                        onChange={setIsActive}
                        description="Inactive users cannot sign in until activated."
                    />

                    <Toggle
                        label="Invite user"
                        checked={inviteUser}
                        onChange={setInviteUser}
                        description="Send the admin invite email after creating the user."
                    />

                    <Field label="Company" required>
                        <CompanyCombobox
                            value={company ? formatCompanyLabel(company) : ''}
                            onSelect={(nextCompany) => {
                                setCompany(nextCompany);
                                setTeamIds([]);
                            }}
                        />
                    </Field>

                    <div className={formCheckboxListSurfaceClassName}>
                        <div className="mb-3">
                            <h3 className="text-sm font-semibold text-neutral-950">
                                Teams
                                <span className="text-red-600" aria-hidden="true">
                                    {' '}
                                    *
                                </span>
                            </h3>
                        </div>
                        {!company ? (
                            <p className="text-sm text-neutral-500">Choose a company first.</p>
                        ) : (
                            <div className="grid gap-2 md:grid-cols-2">
                                {company.teams.map((team) => (
                                    <label
                                        key={team.teamId}
                                        className={cn(
                                            'flex items-center gap-2 rounded-md p-2 text-sm',
                                            formFieldGroupControlClassName,
                                        )}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={teamIds.includes(team.teamId)}
                                            onChange={() => toggleTeam(team.teamId)}
                                        />
                                        <span>{formatTeamLabel(team)}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-2 border-t pt-4">
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={createUser.isPending || isBatchCreating}>
                            {mode === 'batch'
                                ? isBatchCreating
                                    ? 'Creating...'
                                    : `Create ${batchUsers.length > 0 ? `${batchUsers.length} ` : ''}Users`
                                : createUser.isPending
                                  ? 'Creating...'
                                  : 'Create User'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function ModeButton({
    active,
    label,
    onClick,
}: {
    active: boolean;
    label: string;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            className={
                active
                    ? 'rounded-md bg-neutral-900 px-3 py-1.5 text-sm font-medium text-white'
                    : 'rounded-md px-3 py-1.5 text-sm font-medium text-neutral-600 hover:bg-neutral-100'
            }
            onClick={onClick}
        >
            {label}
        </button>
    );
}

function BatchUserEditor({
    input,
    inputMode,
    onInputChange,
    onInputModeChange,
    onParse,
    onUpdateUser,
    users,
}: {
    input: string;
    inputMode: BatchInputMode;
    onInputChange: (value: string) => void;
    onInputModeChange: (mode: BatchInputMode) => void;
    onParse: () => void;
    onUpdateUser: (
        userId: string,
        field: keyof Pick<BatchUserDraft, 'email' | 'firstName' | 'lastName'>,
        value: string,
    ) => void;
    users: BatchUserDraft[];
}) {
    return (
        <div className="space-y-4">
            <div className={`space-y-2 ${formFieldGroupSurfaceClassName}`}>
                <p className="text-sm font-semibold text-neutral-950">Batch input format</p>
                <div className="flex flex-col gap-2 sm:flex-row">
                    <label className="flex items-center gap-2 text-sm text-neutral-700">
                        <input
                            type="radio"
                            checked={inputMode === 'emails'}
                            onChange={() => onInputModeChange('emails')}
                        />
                        Emails only
                    </label>
                    <label className="flex items-center gap-2 text-sm text-neutral-700">
                        <input
                            type="radio"
                            checked={inputMode === 'csv'}
                            onChange={() => onInputModeChange('csv')}
                        />
                        CSV: email, first, last
                    </label>
                </div>
            </div>

            <Field label="Users to create" required>
                <textarea
                    aria-label="Users to create"
                    className="min-h-28 w-full rounded-md admin-field-fill px-3 py-2 text-sm text-neutral-900"
                    value={input}
                    onChange={(event) => onInputChange(event.target.value)}
                    placeholder={
                        inputMode === 'emails'
                            ? 'jane.doe@example.com, sam.smith@example.com'
                            : 'jane@example.com, Jane, Doe'
                    }
                />
            </Field>

            <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-neutral-500">
                    Review and edit the parsed users before creating them.
                </p>
                <Button type="button" variant="outline" onClick={onParse}>
                    Parse users
                </Button>
            </div>

            {users.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-neutral-200">
                    <table className="w-full min-w-[640px] text-left text-sm">
                        <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                            <tr>
                                <th className="px-3 py-2">Email</th>
                                <th className="px-3 py-2">First</th>
                                <th className="px-3 py-2">Last</th>
                                <th className="px-3 py-2">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user) => (
                                <tr key={user.id} className="border-t border-neutral-200">
                                    <td className="px-3 py-2">
                                        <Input
                                            type="email"
                                            value={user.email}
                                            onChange={(event) =>
                                                onUpdateUser(user.id, 'email', event.target.value)
                                            }
                                        />
                                    </td>
                                    <td className="px-3 py-2">
                                        <Input
                                            value={user.firstName}
                                            onChange={(event) =>
                                                onUpdateUser(
                                                    user.id,
                                                    'firstName',
                                                    event.target.value,
                                                )
                                            }
                                        />
                                    </td>
                                    <td className="px-3 py-2">
                                        <Input
                                            value={user.lastName}
                                            onChange={(event) =>
                                                onUpdateUser(
                                                    user.id,
                                                    'lastName',
                                                    event.target.value,
                                                )
                                            }
                                        />
                                    </td>
                                    <td className="px-3 py-2 text-xs text-neutral-600">
                                        {user.status === 'error' ? user.error : user.status}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function Field({
    children,
    label,
    required,
}: {
    children: JSX.Element;
    label: string;
    required?: boolean;
}) {
    return (
        <label className="block space-y-1">
            <span className="text-sm font-semibold text-neutral-950">
                {label}
                {required ? (
                    <span className="text-red-600" aria-hidden="true">
                        {' '}
                        *
                    </span>
                ) : null}
            </span>
            {children}
        </label>
    );
}

function Toggle({
    checked,
    description,
    label,
    onChange,
}: {
    checked: boolean;
    description: string;
    label: string;
    onChange: (checked: boolean) => void;
}) {
    return (
        <label className={`flex items-start gap-3 ${formFieldGroupSurfaceClassName}`}>
            <input
                type="checkbox"
                checked={checked}
                onChange={(event) => onChange(event.target.checked)}
                className="mt-1 size-4 rounded border-neutral-300"
            />
            <span>
                <span className="block text-sm font-medium text-neutral-950">{label}</span>
                <span className="block text-xs text-neutral-500">{description}</span>
            </span>
        </label>
    );
}

function CompanyCombobox({
    onSelect,
    value,
}: {
    onSelect: (company: AdminUserEditCompanyOption) => void;
    value: string;
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const optionsQuery = useAdminUserEditOptions(query, 25, open);
    const companies = optionsQuery.data?.pages.flatMap((page) => page.items) ?? [];

    useEffect(() => {
        if (!open) return;

        const handlePointerDown = (event: PointerEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [open]);

    const handleScroll = (event: UIEvent<HTMLDivElement>) => {
        const target = event.currentTarget;
        const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 16;
        if (nearBottom && optionsQuery.hasNextPage && !optionsQuery.isFetchingNextPage) {
            void optionsQuery.fetchNextPage();
        }
    };

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                className="flex h-9 w-full items-center rounded-md admin-field-fill px-3 text-left text-sm text-neutral-900"
                onClick={() => setOpen((current) => !current)}
            >
                <span className={value ? 'truncate' : 'truncate text-neutral-500'}>
                    {value || 'Choose a company'}
                </span>
            </button>
            {open && (
                <div className="absolute z-50 mt-1 w-full rounded-md border border-neutral-200 bg-white p-2 shadow-lg">
                    <Input
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Search company name or ID"
                        autoFocus
                    />
                    <div className="mt-2 max-h-56 overflow-y-auto" onScroll={handleScroll}>
                        {companies.map((company) => (
                            <button
                                key={company.companyId}
                                type="button"
                                className="block w-full rounded px-2 py-2 text-left text-sm hover:bg-neutral-50"
                                onClick={() => {
                                    onSelect(company);
                                    setOpen(false);
                                }}
                            >
                                <span className="block font-medium text-neutral-950">
                                    {formatCompanyLabel(company)}
                                </span>
                                <span className="block text-xs text-neutral-500">
                                    {company.teams.length} teams:{' '}
                                    {company.teams.slice(0, 3).map(formatTeamLabel).join(', ')}
                                    {company.teams.length > 3 ? '...' : ''}
                                </span>
                            </button>
                        ))}
                        {optionsQuery.isFetching && (
                            <p className="px-2 py-2 text-xs text-neutral-500">Loading...</p>
                        )}
                        {!optionsQuery.isFetching && companies.length === 0 && (
                            <p className="px-2 py-2 text-xs text-neutral-500">
                                No companies found.
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
